import React from 'react';
import { motion } from 'framer-motion';
export function ProblemSection() {
  const problems = [{
    title: 'Conflict that escalates into formal complaints',
    description: 'Minor tensions left unaddressed harden into formal grievances, costing organisations $50K-$200K in legal fees, leadership time, and productivity loss.',
    color: 'border-brand-magenta'
  }, {
    title: 'Culture issues invisible to leadership',
    description: 'Leaders see the surface. Deep-seated cultural risks – bullying, exclusion, toxic norms – remain hidden until they cause significant damage or talent exodus.',
    color: 'border-brand-teal'
  }, {
    title: 'Leaders avoiding difficult conversations',
    description: 'Without the right tools or confidence, leaders delay hard conversations. Poor behaviour becomes normalised. High performers leave. Culture erodes.',
    color: 'border-brand-orange'
  }, {
    title: "Employees who don't trust internal pathways",
    description: 'When internal channels feel unsafe or biased, employees leave silently — taking institutional knowledge, morale and trust with them.',
    color: 'border-brand-plum'
  }];
  return <section className="py-24 bg-brand-off-white relative" data-id="element-382">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-383">
        <div className="grid lg:grid-cols-2 gap-16 items-start" data-id="element-384">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="sticky top-32 relative" data-id="element-385">
            {/* Large Decorative Number - Fixed collision by moving further left/up and reducing opacity */}
            <div className="absolute -left-16 -top-16 text-[140px] font-serif text-brand-deep-purple opacity-[0.03] leading-none select-none pointer-events-none" data-id="element-386">
              01
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-charcoal mb-6 leading-tight relative z-10" data-id="element-387">
              The issues that stay hidden are often the ones that cost the most.
            </h2>

            <p className="text-lg text-brand-charcoal/80 mb-6 leading-relaxed font-normal" data-id="element-388">
              Workplace conflict rarely starts as a legal problem. It starts as
              tension, avoidance, mistrust, poor communication, or a concern
              people don't feel safe raising.
            </p>

            <p className="text-lg text-brand-charcoal/80 mb-6 leading-relaxed font-normal" data-id="element-389">
              EquipMe helps organisations surface issues earlier, respond
              fairly, and build healthier systems before problems become
              entrenched.
            </p>

            <p className="text-lg text-brand-charcoal/90 mb-10 leading-relaxed font-medium" data-id="element-390">
              Imagine if your organisation could surface these issues months
              earlier — before the resignation letter, the formal complaint, or
              the call from a lawyer. That's what independent support makes
              possible.
            </p>

            <div className="bg-white p-8 border-l-4 border-brand-teal shadow-sm rounded-sm" data-id="element-391">
              <p className="text-brand-charcoal font-medium text-lg italic leading-relaxed" data-id="element-392">
                "Australian organisations lose an estimated{' '}
                <span className="text-2xl font-bold text-brand-magenta" data-id="element-393">
                  $36 billion
                </span>{' '}
                every year to workplace conflict and poor culture – through
                absenteeism, turnover, reduced productivity, and legal costs."
              </p>
              <p className="text-sm text-brand-teal mt-4 font-semibold uppercase tracking-wider" data-id="element-394">
                — Safe Work Australia, 2024
              </p>
            </div>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6" data-id="element-395">
            {problems.map((problem, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`bg-white p-8 rounded-sm shadow-sm border-l-4 ${problem.color} hover:shadow-lg transition-all group`} data-id="element-396">
                <h3 className="text-xl font-serif text-brand-charcoal mb-4 group-hover:text-brand-teal transition-colors" data-id="element-397">
                  {problem.title}
                </h3>
                <p className="text-brand-charcoal/80 text-[15px] leading-relaxed font-normal" data-id="element-398">
                  {problem.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}